import { mainIconSize } from '@/constants';
import { downSize } from '@/helpers/text';
import type { DecodeStatus, InstrumentRunStatus } from '@/types';
import type { UseHoverProps } from '@floating-ui/react';
import { Badge } from '@nautilusbio/nb-design-system/Badge';
import type { BadgeVariants } from '@nautilusbio/nb-design-system/Badge';
import type { TextSizes } from '@nautilusbio/nb-design-system/helpers';
import { cssVar } from '@nautilusbio/nb-design-system/styles/css-variables';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import React, { type FC } from 'react';

import styles from './styles.module.css';

type StatusConfig = {
  accentColor?: string;
  color?: string;
  variant: BadgeVariants;
};

const statuses: Record<InstrumentRunStatus | DecodeStatus, StatusConfig> = {
  // InstrumentRunStatus
  COMPLETED: {
    variant: 'success',
  },
  DRAFT: {
    variant: 'default',
  },
  SUBMITTED: {
    accentColor: cssVar.color.tertiary3,
    color: cssVar.color.lightBlue,
    variant: 'unstyled',
  },
  STARTED: {
    variant: 'info',
  },
  // DecodeStatus
  Draft: {
    variant: 'default',
  },
  Pending: {
    variant: 'warning',
  },
  Analyzing: {
    variant: 'info',
  },
  Failed: {
    variant: 'danger',
  },
  Completed: {
    variant: 'success',
  },
};

const hoverOptions: UseHoverProps = {
  delay: {
    close: 0,
    open: 1000,
  },
};

export type StatusBadgeProps = {
  className?: string;
  showIcon?: LucideIcon;
  size?: TextSizes;
  status: InstrumentRunStatus | DecodeStatus;
};

const StatusBadge: FC<StatusBadgeProps> = ({ className, showIcon, size = 'md', status }) => {
  const { accentColor, color, variant } = statuses[status];
  const isTiny = size === 'xs3';
  const Icon = showIcon;
  const textSize = downSize(size);

  return (
    <Tooltip
      content={status}
      hoverOptions={hoverOptions}
      isDisabled={!isTiny}
      placement="left"
      trigger={
        <Badge
          accentColor={accentColor}
          className={clsx(styles.badge, isTiny && styles.sm_xs3, !!showIcon && styles.showIcon, className)}
          color={color}
          size={size}
          variant={variant}
        >
          <Text as="span" size={textSize}>
            {status}
          </Text>

          {Icon && <Icon size={mainIconSize} />}
        </Badge>
      }
    />
  );
};

export default StatusBadge;
